import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Skeleton,
  Modal,
  Input,
  RadioGroup,
  FileUpload,
  ErrorBoundary,
  Timeline,
  useToast,
} from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatDocAmount } from '@/utils/purchase-currency';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import {
  usePurchaseOrder,
  useApprovePO,
  useRejectPO,
  useCancelPO,
  useReverseReceipt,
  useNotifySupplier,
  useDownloadPOPdf,
  POStatusBadge,
  matchLine,
  matchTotals,
  priceTolerance,
  MATCH_LABELS,
  costToConfirmCount,
  GstTaxCell,
  PlaceOfSupply,
  isGstSplit,
} from '@/features/purchase-orders';
import type { MatchStatus } from '@/features/purchase-orders';
import type { PurchaseOrderItem, NotifyChannel, GoodsReceipt } from '@/features/purchase-orders';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { PendingAmendmentBanner, PoRevisionHistory, CloseRemainingModal } from './PoAmendmentPanels';
import { ReturnToSupplierModal, PoReturnsCard, returnableQuantity } from '@/features/purchase-returns';
import { CheckIcon, XIcon, BanIcon, TruckIcon, SendIcon, DownloadIcon, ClipboardIcon, TrashIcon, PencilIcon } from './icons';
import { useAddPoDocument, useDeletePoDocument, uploadPurchaseDocument, DocumentUploadError, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, formatBytes } from '@/features/vendor-bills';

// ─── Status rail config — left colored bar (No Borders principle) ───────────

const STATUS_RAIL_BASE =
  "relative pl-5 before:content-[''] before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full";

const STATUS_RAIL: Record<string, { rail: string; halo: string }> = {
  DRAFT: {
    rail: 'before:bg-gradient-to-b before:from-neutral-400 before:to-neutral-500',
    halo: 'bg-neutral-200/40 dark:bg-neutral-700/40',
  },
  ORDERED: {
    rail: 'before:bg-gradient-to-b before:from-brand-400 before:to-brand-600',
    halo: 'bg-brand-100/30 dark:bg-brand-900/30',
  },
  PARTIALLY_RECEIVED: {
    rail: 'before:bg-gradient-to-b before:from-warning-400 before:to-warning-600',
    halo: 'bg-warning-100/30 dark:bg-warning-900/30',
  },
  RECEIVED: {
    rail: 'before:bg-gradient-to-b before:from-success-400 before:to-success-600',
    halo: 'bg-success-100/30 dark:bg-success-900/30',
  },
  CANCELLED: {
    rail: 'before:bg-gradient-to-b before:from-danger-400 before:to-danger-600',
    halo: 'bg-danger-100/30 dark:bg-danger-900/30',
  },
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  DRAFT: 'This purchase order is pending approval before being sent to the supplier.',
  ORDERED: 'This PO has been approved and sent to the supplier. Awaiting delivery.',
  PARTIALLY_RECEIVED: 'Some items have been received. Continue receiving remaining goods.',
  RECEIVED: 'All items have been fully received and stock has been updated.',
  CANCELLED: 'This purchase order has been cancelled.',
};

// ─── Item progress bar ──────────────────────────────────────────────────────

function ItemProgressBar({ ordered, received }: { ordered: number; received: number }) {
  const pct = ordered > 0 ? Math.min((received / ordered) * 100, 100) : 0;
  const over = received > ordered;
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 rounded-full bg-neutral-200 dark:bg-neutral-700 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${
            over ? 'bg-warning-500' : pct >= 100 ? 'bg-success-500' : pct > 0 ? 'bg-teal-500' : ''
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs tabular-nums whitespace-nowrap ${over ? 'text-warning-700 dark:text-warning-300 font-medium' : 'text-neutral-500 dark:text-neutral-400'}`}>
        {received}/{ordered}{over ? ` (+${received - ordered})` : ''}
      </span>
    </div>
  );
}

const MATCH_PILL: Record<MatchStatus, string> = {
  MATCHED: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300',
  QTY_VARIANCE: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300',
  PRICE_VARIANCE: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
  QTY_AND_PRICE_VARIANCE: 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
  AWAITING_RECEIPT: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  AWAITING_BILL: 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
};

function signed(paise: number, currency: string) {
  return `${paise > 0 ? '+' : paise < 0 ? '−' : ''}${formatDocAmount(Math.abs(paise), currency)}`;
}

// ─── Detail Content ─────────────────────────────────────────────────────────

function PurchaseOrderDetailContent() {
  const tenantCurrency = useTenantCurrency();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  // Modals
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  // Close remaining: undefined = closed dialog, null = every open line, [ids] = those lines.
  const [closeItemIds, setCloseItemIds] = useState<string[] | null | undefined>(undefined);

  // Modal form state
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [notifyChannel, setNotifyChannel] = useState<NotifyChannel>('EMAIL');
  const [notifyRecipient, setNotifyRecipient] = useState('');

  const { data: po, isLoading, error } = usePurchaseOrder(id);
  // docs/multi-currency-purchasing-plan.md D9: a PO's amounts are in its own currency.
  const currency = po?.currency_code ?? tenantCurrency;
  const approveMutation = useApprovePO();
  const rejectMutation = useRejectPO();
  const cancelMutation = useCancelPO();
  // QA PU3-004: OWNER reverses a receipt entered by mistake (whole or part).
  const reverseMutation = useReverseReceipt();
  const [reverseGrn, setReverseGrn] = useState<GoodsReceipt | null>(null);
  const [reverseQty, setReverseQty] = useState<Record<string, string>>({});
  const [reverseReason, setReverseReason] = useState('');
  // QA PU3-005: goods from a receipt go back to the supplier (MANAGER drafts, OWNER posts).
  const [returnGrn, setReturnGrn] = useState<GoodsReceipt | null>(null);
  const notifyMutation = useNotifySupplier();
  const downloadPdfMutation = useDownloadPOPdf();
  const addDocMutation = useAddPoDocument();
  const deleteDocMutation = useDeletePoDocument();
  const [uploadingDoc, setUploadingDoc] = useState(false);

  async function handleUploadDocument(files: File[]) {
    const f = files[0];
    if (!f || !id) return;
    setUploadingDoc(true);
    try {
      const payload = await uploadPurchaseDocument(f);
      addDocMutation.mutate({ poId: id, payload }, {
        onSuccess: () => toast({ title: `${f.name} attached`, variant: 'success' }),
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      });
    } catch (err) {
      toast({ title: err instanceof DocumentUploadError ? err.message : 'Upload failed', variant: 'error' });
    } finally {
      setUploadingDoc(false);
    }
  }

  // Stream the A4 PDF from the API and hand it to the browser as a download.
  // The filename comes from Content-Disposition (PO number), with a fallback.
  function handleDownloadPdf() {
    if (!id || !po) return;
    downloadPdfMutation.mutate(id, {
      onSuccess: ({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename ?? `${po.po_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast({ title: `${po.po_number}.pdf downloaded`, variant: 'success' });
      },
      onError: (err: Error) => {
        toast({ title: err.message || 'Failed to download PO', variant: 'error' });
      },
    });
  }

  // Auto-open modals from URL action param
  useEffect(() => {
    if (!po) return;
    const action = searchParams.get('action');
    if (action === 'approve' && po.status === 'DRAFT' && isOwner) setShowApproveModal(true);
    if (action === 'reject' && po.status === 'DRAFT' && isOwner) setShowRejectModal(true);
    if (action === 'notify' && (po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED')) setShowNotifyModal(true);
  }, [po, searchParams, isOwner]);

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/inventory/purchase-orders');
    return null;
  }

  if (isLoading || !po) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <Skeleton variant="rect" height={200} className="rounded-xl" />
            <Skeleton variant="rect" height={300} className="rounded-xl" />
            <Skeleton variant="rect" height={200} className="rounded-xl" />
          </div>
          <div className="space-y-4">
            <Skeleton variant="rect" height={160} className="rounded-xl" />
            <Skeleton variant="rect" height={200} className="rounded-xl" />
          </div>
        </div>
      </>
    );
  }

  const statusRail = STATUS_RAIL[po.status] ?? STATUS_RAIL.DRAFT;
  const items = po.items ?? [];
  const canApproveReject = po.status === 'DRAFT' && isOwner;
  // Lines still at cost 0: the API refuses approval until each is priced (I-1).
  const costToConfirm = costToConfirmCount(items);
  const canReceive = (po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED') && isManager;
  const canCancel = po.status !== 'RECEIVED' && po.status !== 'CANCELLED' && isOwner;
  // Only approved POs can be sent to the supplier (a DRAFT must be approved
  // first); the API answers 409 otherwise. Email only at launch.
  const canNotify = (po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED') && isManager;
  // POS comments item 10: amend an approved PO, close what the supplier won't send.
  const canAmend = (po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED') && isManager && !po.pending_amendment_id;
  const isOpenLine = (i: PurchaseOrderItem) => i.status !== 'CLOSED' && i.received_quantity < i.quantity;
  const canClose = (po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED') && isManager && items.some(isOpenLine);

  function handleApprove() {
    if (!id) return;
    approveMutation.mutate(
      { id, payload: { notes: approveNotes.trim() || undefined } },
      {
        onSuccess: () => {
          toast({ title: `PO ${po!.po_number} approved`, variant: 'success' });
          setShowApproveModal(false);
          setApproveNotes('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleReject() {
    if (!id || !rejectReason.trim()) return;
    rejectMutation.mutate(
      { id, payload: { reason: rejectReason.trim() } },
      {
        onSuccess: () => {
          toast({ title: `PO ${po!.po_number} rejected`, variant: 'success' });
          setShowRejectModal(false);
          setRejectReason('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleCancel() {
    if (!id || !cancelReason.trim()) return;
    cancelMutation.mutate(
      { id, payload: { reason: cancelReason.trim() } },
      {
        onSuccess: () => {
          toast({ title: `PO ${po!.po_number} cancelled`, variant: 'success' });
          setShowCancelModal(false);
          setCancelReason('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  function handleNotify() {
    if (!id || !notifyRecipient.trim()) return;
    notifyMutation.mutate(
      { id, payload: { channel: notifyChannel, recipient: notifyRecipient.trim() } },
      {
        onSuccess: () => {
          toast({ title: `PO sent to supplier via ${notifyChannel}`, variant: 'success' });
          setShowNotifyModal(false);
          setNotifyRecipient('');
        },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      },
    );
  }

  // Build timeline
  const timelineItems: TimelineItem[] = [
    {
      id: '1',
      type: 'draft',
      action: 'Purchase order created',
      timestamp: po.created_at,
      ...(po.created_by_name ? { actor: po.created_by_name } : {}),
    },
  ];
  if (po.approved_at) {
    timelineItems.push({
      id: '2',
      type: 'approved',
      action: 'Approved and sent to supplier',
      timestamp: po.approved_at,
      ...(po.approved_by_name ? { actor: po.approved_by_name } : {}),
    });
  }
  const receipts = po.receiving_history ?? [];
  if (receipts.length > 0) {
    receipts.forEach((g, idx) => {
      // What the receipt brought in, before any reversal (QA PU3-004).
      const units = g.items.reduce((s, i) => s + i.quantity + (i.reversed_quantity ?? 0), 0);
      const last = idx === receipts.length - 1 && po.status === 'RECEIVED';
      timelineItems.push({
        id: `grn-${g.id}`,
        type: last ? 'completed' : 'inProgress',
        action: `${g.grn_number}: received ${units} units${g.vendor_invoice_number ? ` · invoice ${g.vendor_invoice_number}` : ''}${last ? ' — order complete' : ''}`,
        timestamp: g.created_at,
        ...(g.received_by_name ? { actor: g.received_by_name } : {}),
      });
      for (const r of g.reversals ?? []) {
        timelineItems.push({
          id: `grn-rev-${r.id}`,
          type: 'cancelled',
          action: `${g.grn_number}: reversed ${r.items.reduce((s, i) => s + i.quantity, 0)} units — ${r.reason}`,
          timestamp: r.reversed_at,
          ...(r.reversed_by_name ? { actor: r.reversed_by_name } : {}),
        });
      }
    });
  } else if (po.status === 'PARTIALLY_RECEIVED') {
    timelineItems.push({ id: '3', type: 'inProgress', action: 'Partial goods received', timestamp: po.updated_at });
  } else if (po.status === 'RECEIVED') {
    timelineItems.push({ id: '4', type: 'completed', action: 'All goods received — stock updated', timestamp: po.updated_at });
  }
  // PD10: the API refuses to cancel a PO while any bill on it is not cancelled.
  const blockingBills = (po.vendor_bills ?? []).filter((b) => b.status !== 'CANCELLED');
  for (const vb of po.vendor_bills ?? []) {
    timelineItems.push({ id: `vb-${vb.id}`, type: vb.status === 'PAID' ? 'completed' : 'pending', action: `Vendor bill ${vb.bill_number} (${vb.basis === 'ORDERED' ? 'full order' : 'delivered'}) · ${vb.status.replace('_', ' ').toLowerCase()}`, timestamp: vb.created_at });
  }
  if (po.status === 'CANCELLED') {
    timelineItems.push({
      id: '5',
      type: 'cancelled',
      action: `Cancelled${po.cancel_reason ? `: ${po.cancel_reason}` : ''}`,
      timestamp: po.cancelled_at ?? po.updated_at,
      ...(po.cancelled_by_name ? { actor: po.cancelled_by_name } : {}),
    });
  }

  return (
    <>
      {/* Gradient header — status rail wraps the whole card to signal lifecycle */}
      <div
        className={`${STATUS_RAIL_BASE} ${statusRail.rail} mb-6 animate-fade-in`}
      >
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)]">
          <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-mono text-2xl font-bold tracking-tight text-white">{po.po_number}</h1>
                <POStatusBadge status={po.status} size="md" />
                {(po.revision_no ?? 0) > 0 && (
                  <span className="rounded-full bg-white/20 px-2.5 py-0.5 font-mono text-xs font-semibold text-white ring-1 ring-inset ring-white/30">
                    Rev {po.revision_no}
                  </span>
                )}
              </div>
              <p className="text-teal-100 mt-1.5 truncate">
                {po.supplier_name}
                {po.supplier_id && po.supplier_is_active === false && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-warning-500/20 px-2 py-0.5 text-xs font-medium text-warning-100 ring-1 ring-inset ring-warning-300/40"
                    title="Supplier is deactivated"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-warning-300" />
                    Supplier deactivated
                  </span>
                )}
                {po.expected_delivery_date && (
                  <> <span className="text-teal-200/70">·</span> Expected{' '}
                    <span className="mono-num">
                      {new Date(po.expected_delivery_date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono text-3xl font-bold tabular-nums">
                {formatDocAmount(po.total_amount, currency)}
              </p>
              <p className="text-xs uppercase tracking-wider text-teal-200 mt-0.5">Total Amount</p>
              {po.currency_code && po.base_total_amount != null && (
                <p className="text-sm text-teal-100 mt-1 tabular-nums">
                  ≈ {formatDocAmount(po.base_total_amount, tenantCurrency)} · 1 {po.currency_code} ={' '}
                  {Number(po.exchange_rate).toLocaleString('en-US', { maximumFractionDigits: 8 })} {tenantCurrency}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-slide-up">
        {/* Left: Details + Items + Timeline */}
        <div className="lg:col-span-2 space-y-6">
          <PendingAmendmentBanner po={po} isOwner={isOwner} currency={currency} />
          {/* PO Information */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">
              Order Information
            </h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">PO Number</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">{po.po_number}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Date Created</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                  {new Date(po.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Supplier</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200 font-medium flex items-center gap-2 flex-wrap">
                  {po.supplier_id ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/inventory/suppliers/${po.supplier_id}`)}
                      className="text-teal-600 dark:text-teal-400 hover:underline text-left"
                    >
                      {po.supplier_name}
                    </button>
                  ) : (
                    <span>{po.supplier_name}</span>
                  )}
                  {po.supplier_id && po.supplier_is_active === false && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-900/30 px-2 py-0.5 text-xs font-medium text-warning-800 dark:text-warning-200">
                      <span className="h-1.5 w-1.5 rounded-full bg-warning-500" />
                      Deactivated
                    </span>
                  )}
                  {po.rfq && (
                    <button
                      type="button"
                      onClick={() => navigate(`/inventory/rfqs/${po.rfq?.id}`)}
                      className="inline-flex items-center gap-1 rounded-full bg-brand-50 dark:bg-brand-900/30 px-2 py-0.5 text-xs font-medium text-brand-700 dark:text-brand-200 hover:bg-brand-100 transition-colors"
                      title="Raised from this RFQ"
                    >
                      From {po.rfq.rfq_number ?? 'RFQ'}
                    </button>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Location</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">{po.location_name}</dd>
              </div>
              {po.supplier_phone && (
                <div>
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Phone</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">{po.supplier_phone}</dd>
                </div>
              )}
              {po.supplier_email && (
                <div>
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Email</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200">{po.supplier_email}</dd>
                </div>
              )}
              {po.expected_delivery_date && (
                <div>
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Expected Delivery</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200 tabular-nums">
                    {new Date(po.expected_delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Created By</dt>
                <dd className="text-sm text-neutral-800 dark:text-neutral-200">{po.created_by_name}</dd>
              </div>
              {po.notes && (
                <div className="sm:col-span-2">
                  <dt className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Notes</dt>
                  <dd className="text-sm text-neutral-800 dark:text-neutral-200">{po.notes}</dd>
                </div>
              )}
            </dl>
          </div>

          {/* Items Table */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">
              Items ({items.length})
            </h3>
            <PlaceOfSupply supply={po.supply} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Product</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Ordered</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide w-40">Received</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Billed</th>
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Unit</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Unit Cost</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Discount</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Tax</th>
                    <th className="text-right py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item: PurchaseOrderItem) => (
                    <tr key={item.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0">
                      <td className="py-2.5 pr-3 text-neutral-800 dark:text-neutral-200 font-medium">
                        {item.product_name}
                        {item.status === 'CLOSED' && (
                          <div className="mt-0.5 text-[11px] font-normal text-neutral-500 dark:text-neutral-400">
                            <span className="mr-1 rounded-full bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">Closed</span>
                            {item.closed_quantity} not coming{item.closed_reason ? ` — ${item.closed_reason}` : ''}
                          </div>
                        )}
                        {canClose && isOpenLine(item) && (
                          <button
                            type="button"
                            className="mt-0.5 block text-[11px] font-normal text-brand-600 hover:underline dark:text-brand-400"
                            onClick={() => setCloseItemIds([item.id])}
                          >
                            Close the rest
                          </button>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                        {item.quantity}
                      </td>
                      <td className="py-2.5 pr-3">
                        <ItemProgressBar ordered={item.quantity} received={item.received_quantity} />
                      </td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">
                        <span className={`text-sm ${(item.billed_quantity ?? 0) > 0 ? 'text-neutral-800 dark:text-neutral-200' : 'text-neutral-400'}`}>
                          {item.billed_quantity ?? 0}/{item.quantity}
                        </span>
                        {(item.billed_quantity ?? 0) > item.received_quantity && (
                          <div className="text-[10px] text-warning-700 dark:text-warning-300 whitespace-nowrap">ahead of delivery</div>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-neutral-500 dark:text-neutral-400">
                        {item.unit}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                        {Number(item.unit_cost) > 0 ? (
                          formatDocAmount(item.unit_cost, currency)
                        ) : (
                          <span className="inline-flex rounded-full bg-warning-100 dark:bg-warning-900/40 px-2 py-0.5 text-[11px] font-semibold text-warning-700 dark:text-warning-300 whitespace-nowrap">
                            Cost to be confirmed
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                        {(item.line_discount ?? 0) > 0 ? (
                          <>
                            {'−'}{formatDocAmount(item.line_discount, currency)}
                            {item.discount_type === 'PERCENTAGE' && (
                              <span className="text-xs text-neutral-500"> ({item.discount_value / 100}%)</span>
                            )}
                          </>
                        ) : '—'}
                      </td>
                      <td className="py-2.5 pr-3 text-right text-neutral-800 dark:text-neutral-200 tabular-nums">
                        <GstTaxCell supply={po.supply} gstRate={item.gst_rate ?? 0} lineTax={item.line_tax ?? 0} currency={currency} />
                      </td>
                      <td className="py-2.5 text-right text-neutral-900 dark:text-neutral-100 font-semibold tabular-nums">
                        {formatDocAmount(item.line_total ?? item.total_cost, currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals — subtotal / discount / tax breakdown above the payable */}
            <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500 dark:text-neutral-400">Subtotal</span>
                <span className="font-mono tabular-nums text-neutral-800 dark:text-neutral-200">{formatDocAmount(po.subtotal ?? po.total_amount, currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500 dark:text-neutral-400">Discount</span>
                <span className="font-mono tabular-nums text-success-600 dark:text-success-400">
                  {(po.discount_total ?? 0) > 0 ? `−${formatDocAmount(po.discount_total, currency)}` : '—'}
                </span>
              </div>
              {isGstSplit(po.supply) && po.tax_lines && po.tax_lines.length > 0 ? (
                po.tax_lines.map((l) => (
                  <div key={`${l.kind}-${l.ratePct}`} className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">{l.kind} {l.ratePct}%</span>
                    <span className="font-mono tabular-nums text-neutral-800 dark:text-neutral-200">{formatDocAmount(l.amount, currency)}</span>
                  </div>
                ))
              ) : (
                <div className="flex justify-between">
                  <span className="text-neutral-500 dark:text-neutral-400">Tax</span>
                  <span className="font-mono tabular-nums text-neutral-800 dark:text-neutral-200">
                    {(po.tax_total ?? 0) > 0 ? formatDocAmount(po.tax_total, currency) : '—'}
                  </span>
                </div>
              )}
              <div className="flex justify-between font-bold pt-2 border-t border-neutral-100 dark:border-neutral-800">
                <span className="text-neutral-900 dark:text-white">Total</span>
                <span className="font-mono tabular-nums text-neutral-900 dark:text-white">{formatDocAmount(po.total_amount, currency)}</span>
              </div>
            </div>
          </div>

          {/* 3-way match — PO vs goods received vs vendor bills (advisory, ex-tax) */}
          {['ORDERED', 'PARTIALLY_RECEIVED', 'RECEIVED'].includes(po.status) && items.length > 0 && (() => {
            const totals = matchTotals(items, currency);
            return (
              <div className={`${GLASS} p-6 animate-fade-in`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">3-way match</h3>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Ordered vs received vs billed, ex-tax. Variances are flagged, not blocked.</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px] font-semibold">
                    {totals.matched_lines > 0 && <span className={`rounded-full px-2 py-0.5 ${MATCH_PILL.MATCHED}`}>{totals.matched_lines} matched</span>}
                    {totals.variance_lines > 0 && <span className={`rounded-full px-2 py-0.5 ${MATCH_PILL.QTY_VARIANCE}`}>{totals.variance_lines} variance</span>}
                    {totals.pending_lines > 0 && <span className={`rounded-full px-2 py-0.5 ${MATCH_PILL.AWAITING_RECEIPT}`}>{totals.pending_lines} pending</span>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: 'PO value', value: totals.ordered_value, cls: 'from-brand-500 to-indigo-500' },
                    { label: 'Received value', value: totals.received_value, cls: 'from-teal-500 to-success-500' },
                    { label: 'Billed value', value: totals.billed_value, cls: 'from-violet-500 to-pink-500' },
                  ].map((k) => (
                    <div key={k.label} className="rounded-xl border border-neutral-100 dark:border-neutral-700 bg-white/70 dark:bg-neutral-900/40 p-3">
                      <div className={`h-1 w-8 rounded-full bg-gradient-to-r ${k.cls} mb-2`} />
                      <div className="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{k.label}</div>
                      <div className="text-base font-bold tabular-nums text-neutral-900 dark:text-white">{formatDocAmount(k.value, currency)}</div>
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-neutral-100 dark:border-neutral-800 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                        <th className="text-left py-2 pr-3">Product</th>
                        <th className="text-right py-2 pr-3">Ordered</th>
                        <th className="text-right py-2 pr-3">Received</th>
                        <th className="text-right py-2 pr-3">Billed</th>
                        <th className="text-right py-2 pr-3">Δ Qty</th>
                        <th className="text-right py-2 pr-3">Δ Price / unit</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => {
                        const m = matchLine(item, currency);
                        return (
                          <tr key={item.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 even:bg-neutral-50/60 dark:even:bg-neutral-800/30 hover:bg-brand-50/60 dark:hover:bg-brand-900/10 transition-colors">
                            <td className="py-2 pr-3 font-medium text-neutral-800 dark:text-neutral-200">{item.product_name}</td>
                            <td className="py-2 pr-3 text-right tabular-nums text-neutral-800 dark:text-neutral-200">
                              {item.quantity} {item.unit}
                              <div className="text-[11px] text-neutral-500">{formatDocAmount(item.ordered_value ?? 0, currency)}</div>
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums text-neutral-800 dark:text-neutral-200">
                              {item.received_quantity} {item.unit}
                              <div className="text-[11px] text-neutral-500">{formatDocAmount(item.received_value ?? 0, currency)}</div>
                            </td>
                            <td className="py-2 pr-3 text-right tabular-nums text-neutral-800 dark:text-neutral-200">
                              {item.billed_quantity ?? 0} {item.unit}
                              <div className="text-[11px] text-neutral-500">{formatDocAmount(item.billed_value ?? 0, currency)}</div>
                            </td>
                            <td className={`py-2 pr-3 text-right tabular-nums ${m.qty_delta !== 0 ? 'text-warning-700 dark:text-warning-300 font-medium' : 'text-neutral-400'}`}>
                              {m.qty_delta > 0 ? `+${m.qty_delta}` : m.qty_delta}
                              {m.billed_qty_delta !== 0 && item.billed_quantity ? <div className="text-[11px] font-normal">bill {m.billed_qty_delta > 0 ? '+' : ''}{m.billed_qty_delta} vs recv</div> : null}
                            </td>
                            <td className={`py-2 pr-3 text-right tabular-nums ${m.price_delta != null && Math.abs(m.price_delta) > priceTolerance(currency) ? 'text-danger-600 dark:text-danger-400 font-medium' : 'text-neutral-400'}`}>
                              {m.price_delta == null ? '—' : signed(m.price_delta, currency)}
                            </td>
                            <td className="py-2">
                              <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${MATCH_PILL[m.status]}`}>{MATCH_LABELS[m.status]}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Goods receipts */}
          <PoRevisionHistory po={po} currency={currency} />

          {(po.receiving_history?.length ?? 0) > 0 && (
            <div className={`${GLASS} p-6`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Receipts ({po.receiving_history.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {po.receiving_history.map((g) => (
                  <li key={g.id} className="py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div>
                        <span className="font-mono font-semibold text-neutral-800 dark:text-neutral-200">{g.grn_number}</span>
                        <span className="text-neutral-500"> · {new Date(g.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{g.received_by_name ? ` · ${g.received_by_name}` : ''}</span>
                        {g.vendor_invoice_number && <span className="ml-2 inline-flex rounded-full bg-orange-50 dark:bg-orange-900/30 px-2 py-0.5 text-xs text-orange-700 dark:text-orange-200">Invoice {g.vendor_invoice_number}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {isManager && po.status !== 'CANCELLED' && g.items.some((i) => i.quantity > 0) && !(po.vendor_bills ?? []).some((b) => b.goods_receipt_id === g.id && b.status !== 'CANCELLED') && (
                          <Button variant="secondary" size="sm" onClick={() => navigate(`/vendor-bills/new?po_id=${po.id}&grn_id=${g.id}&basis=RECEIVED`)}>Bill this receipt</Button>
                        )}
                        {/* Same rule as the API: no posted bill raised from this receipt, something still in. */}
                        {isOwner && po.status !== 'CANCELLED' && g.items.some((i) => i.quantity > 0) && !(po.vendor_bills ?? []).some((b) => b.goods_receipt_id === g.id && b.status !== 'CANCELLED' && b.status !== 'DRAFT') && (
                          <Button variant="ghost" size="sm" className="text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"
                            onClick={() => { setReverseGrn(g); setReverseReason(''); setReverseQty(Object.fromEntries(g.items.map((i) => [i.id, String(i.quantity)]))); }}>
                            Reverse receipt
                          </Button>
                        )}
                        {isManager && po.status !== 'CANCELLED' && g.items.some((i) => returnableQuantity(i) > 0) && (
                          <Button variant="ghost" size="sm" className="text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-900/20"
                            onClick={() => setReturnGrn(g)}>
                            Return to supplier
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                      {g.items.map((i) => `${i.item_name} × ${i.quantity} ${i.unit}${i.reversed_quantity ? ` (${i.reversed_quantity} reversed)` : ''}${i.returned_quantity ? ` (${i.returned_quantity} returned)` : ''}`).join(' · ')}
                    </div>
                    {(g.reversals ?? []).map((r) => (
                      <div key={r.id} className="mt-1 text-xs text-danger-600 dark:text-danger-400">
                        Reversed {r.items.reduce((s, i) => s + i.quantity, 0)} units on {new Date(r.reversed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{r.reversed_by_name ? ` by ${r.reversed_by_name}` : ''} · {r.reason}
                      </div>
                    ))}
                    {g.items.some((i) => (i.serial_numbers?.length ?? 0) > 0) && (
                      <details className="mt-1 text-xs">
                        <summary className="cursor-pointer text-teal-700 dark:text-teal-300 hover:underline">
                          {g.items.reduce((n, i) => n + (i.serial_numbers?.length ?? 0), 0)} serial numbers
                        </summary>
                        <div className="mt-1 space-y-1">
                          {g.items.filter((i) => (i.serial_numbers?.length ?? 0) > 0).map((i) => (
                            <div key={i.id} className="flex flex-wrap gap-1">
                              <span className="text-neutral-500 dark:text-neutral-400 mr-1">{i.item_name}:</span>
                              {(i.serial_numbers ?? []).map((sn) => (
                                <span key={sn} className="font-mono rounded bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 text-neutral-700 dark:text-neutral-200">{sn}</span>
                              ))}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <PoReturnsCard poId={po.id} currency={currency} isOwner={isOwner} isManager={isManager} />

          {/* Vendor bills */}
          {(po.vendor_bills?.length ?? 0) > 0 && (
            <div className={`${GLASS} p-6`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Vendor Bills ({po.vendor_bills?.length})</h3>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {(po.vendor_bills ?? []).map((b) => (
                  <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <button type="button" onClick={() => navigate(`/vendor-bills/${b.id}`)} className="font-mono font-medium text-orange-600 dark:text-orange-400 hover:underline">{b.bill_number}</button>
                    <span className="text-xs uppercase tracking-wide text-neutral-500">{b.basis === 'ORDERED' ? 'full order' : 'delivered'}{b.vendor_invoice_number ? ` · ${b.vendor_invoice_number}` : ''}</span>
                    {b.status === 'DRAFT' ? (
                      <span className="rounded-full bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300 px-2 py-0.5 text-[11px] font-semibold" title="Not posted yet — not counted as billed">draft</span>
                    ) : (
                      <span className="text-xs text-neutral-500">{b.status.replace('_', ' ').toLowerCase()}</span>
                    )}
                    <span className="tabular-nums"><span className="font-semibold">{formatDocAmount(b.total_amount, currency)}</span>{b.balance_due > 0 && <span className="text-xs text-neutral-500"> · {formatDocAmount(b.balance_due, currency)} due</span>}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Documents */}
          <div className={`${GLASS} p-6`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Documents ({po.documents?.length ?? 0})</h3>
              {isManager && (
                <div className="w-44"><FileUpload variant="button" accept={DOCUMENT_ACCEPT} maxSize={DOCUMENT_MAX_BYTES} loading={uploadingDoc} onUpload={(files: File[]) => { void handleUploadDocument(files); }} /></div>
              )}
            </div>
            {(po.documents?.length ?? 0) === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No vendor invoices or challans attached yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {(po.documents ?? []).map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate text-neutral-800 dark:text-neutral-200">{d.file_name ?? d.file_key.split('/').pop()}</div>
                      <div className="text-xs text-neutral-500">{d.kind.replace('_', ' ').toLowerCase()}{d.grn_number ? ` · ${d.grn_number}` : ''}{d.vendor_bill_number ? ` · ${d.vendor_bill_number}` : ''} · {formatBytes(d.size_bytes)} · {d.uploaded_by_name ?? ''}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-teal-600 hover:underline"><DownloadIcon className="h-4 w-4" />View</a>
                      {isManager && (
                        <button type="button" title="Remove" onClick={() => deleteDocMutation.mutate({ poId: po.id, docId: d.id }, { onSuccess: () => toast({ title: 'Document removed', variant: 'success' }), onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}
                          className="p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20"><TrashIcon className="h-4 w-4" /></button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Timeline */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">
              Activity
            </h3>
            <Timeline items={timelineItems} />
          </div>
        </div>

        {/* Right: Status + Actions */}
        <div className="space-y-4">
          {/* Status Card with rail */}
          <div className={`${GLASS_FORM} ${STATUS_RAIL_BASE} ${statusRail.rail} p-5`}>
            <div className={`absolute inset-0 -z-10 rounded-2xl ${statusRail.halo}`} />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
              Status
            </h3>
            <POStatusBadge status={po.status} size="md" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">
              {STATUS_DESCRIPTIONS[po.status]}
            </p>
          </div>

          {/* Actions Card — Download PO is available in every status, so the
              card always renders for managers even when nothing else applies. */}
          {(canApproveReject || canReceive || canCancel || canNotify || isManager) && (
            <div className={`${GLASS_FORM} p-5`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Actions</h3>
              <div className="space-y-2">
                {canApproveReject && (
                  <>
                    <Button
                      variant="primary"
                      className="w-full"
                      onClick={() => setShowApproveModal(true)}
                      disabled={costToConfirm > 0}
                    >
                      <CheckIcon className="h-4 w-4 mr-1.5" />
                      Approve
                    </Button>
                    {costToConfirm > 0 && (
                      <p className="text-xs text-warning-700 dark:text-warning-300">
                        Confirm the cost of {costToConfirm} line(s) with Edit PO before approving.
                      </p>
                    )}
                    <Button variant="secondary" className="w-full" onClick={() => setShowRejectModal(true)}>
                      <XIcon className="h-4 w-4 mr-1.5" />
                      Reject
                    </Button>
                  </>
                )}
                {canReceive && (
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => navigate(`/inventory/purchase-orders/${id}/receive`)}
                  >
                    <TruckIcon className="h-4 w-4 mr-1.5" />
                    {po.status === 'PARTIALLY_RECEIVED' ? 'Receive More' : 'Receive Goods'}
                  </Button>
                )}
                {canAmend && (
                  <Button variant="secondary" className="w-full" onClick={() => navigate(`/inventory/purchase-orders/${po.id}/amend`)}>
                    <PencilIcon className="h-4 w-4 mr-1.5" />
                    Amend PO
                  </Button>
                )}
                {canClose && (
                  <Button variant="secondary" className="w-full" onClick={() => setCloseItemIds(null)}>
                    <BanIcon className="h-4 w-4 mr-1.5" />
                    Close remaining
                  </Button>
                )}
                {canNotify && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      setNotifyRecipient(po.supplier_email ?? '');
                      setNotifyChannel('EMAIL');
                      setShowNotifyModal(true);
                    }}
                  >
                    <SendIcon className="h-4 w-4 mr-1.5" />
                    Notify Supplier
                  </Button>
                )}
                {isManager && ['ORDERED', 'PARTIALLY_RECEIVED', 'RECEIVED'].includes(po.status) && items.some((i) => (i.billed_quantity ?? 0) < Math.max(i.quantity, i.received_quantity)) && (
                  <Button variant="secondary" className="w-full" onClick={() => navigate(`/vendor-bills/new?po_id=${po.id}&basis=${po.status === 'ORDERED' ? 'ORDERED' : 'RECEIVED'}`)}>
                    <ClipboardIcon className="h-4 w-4 mr-1.5" />
                    Create Vendor Bill
                  </Button>
                )}
                {isManager && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={handleDownloadPdf}
                    loading={downloadPdfMutation.isPending}
                  >
                    <DownloadIcon className="h-4 w-4 mr-1.5" />
                    Download PO
                  </Button>
                )}
                {isManager && (
                  <Button variant="secondary" className="w-full" onClick={() => navigate(`/inventory/rfqs/new?from_po_id=${po.id}`)}>
                    <ClipboardIcon className="h-4 w-4 mr-1.5" />
                    Create RFQ from this PO
                  </Button>
                )}
                {/* Editing is DRAFT-only and OWNER-only, matching
                    PUT /purchase-orders/:id. Once approved, a PO can have
                    receipts and bills against it, so it is changed with
                    Amend PO instead (lines changed in place, as a revision). */}
                {canApproveReject && (
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => navigate(`/inventory/purchase-orders/${po.id}/edit`)}
                  >
                    <PencilIcon className="h-4 w-4 mr-1.5" />
                    Edit PO
                  </Button>
                )}
                {canCancel && (
                  <Button variant="danger" className="w-full" onClick={() => setShowCancelModal(true)}>
                    <BanIcon className="h-4 w-4 mr-1.5" />
                    Cancel PO
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Cancellation info */}
          {po.status === 'CANCELLED' && po.cancel_reason && (
            <div className={`${GLASS_FORM} ${STATUS_RAIL_BASE} ${STATUS_RAIL.CANCELLED.rail} p-5`}>
              <div className={`absolute inset-0 -z-10 rounded-2xl ${STATUS_RAIL.CANCELLED.halo}`} />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-2">
                Cancellation
              </h3>
              <p className="text-sm text-neutral-700 dark:text-neutral-300">{po.cancel_reason}</p>
              {po.cancelled_at && (
                <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-1.5 mono-num">
                  {new Date(po.cancelled_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      <Modal
        open={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Approve Purchase Order"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowApproveModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleApprove} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Approve <strong>{po.po_number}</strong> ({formatDocAmount(po.total_amount, currency)}) from <strong>{po.supplier_name}</strong>.
            The PO status will change to <strong>Ordered</strong>.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
              placeholder="Add optional notes..."
              rows={3}
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none transition-colors"
              maxLength={500}
            />
          </div>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Reject Purchase Order"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={handleReject}
              disabled={rejectMutation.isPending || !rejectReason.trim()}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Reject <strong>{po.po_number}</strong>. This will cancel the purchase order.
          </p>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this PO is being rejected..."
              rows={3}
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-danger-500 focus:ring-1 focus:ring-danger-500 outline-none transition-colors"
              maxLength={500}
            />
          </div>
        </div>
      </Modal>

      <ReturnToSupplierModal poId={po.id} grn={returnGrn} locationName={po.location_name ?? null} currency={currency}
        isOwner={isOwner} onClose={() => setReturnGrn(null)} />

      {/* Cancel Modal */}
      <Modal
        open={reverseGrn != null}
        onClose={() => setReverseGrn(null)}
        title={`Reverse receipt ${reverseGrn?.grn_number ?? ''}`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setReverseGrn(null)}>Keep</Button>
            <Button
              variant="danger"
              loading={reverseMutation.isPending}
              disabled={!reverseReason.trim() || !reverseGrn?.items.some((i) => Number(reverseQty[i.id]) > 0)
                || !!reverseGrn?.items.some((i) => Number(reverseQty[i.id] || 0) < 0 || Number(reverseQty[i.id] || 0) > i.quantity)}
              onClick={() => {
                if (!reverseGrn) return;
                const items = reverseGrn.items
                  .filter((i) => Number(reverseQty[i.id]) > 0)
                  .map((i) => ({ goods_receipt_item_id: i.id, quantity: Number(reverseQty[i.id]) }));
                reverseMutation.mutate(
                  { id: po.id, grnId: reverseGrn.id, payload: { reason: reverseReason.trim(), items } },
                  {
                    onSuccess: () => { toast({ title: `${reverseGrn.grn_number} reversed`, variant: 'success' }); setReverseGrn(null); },
                    onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
                  },
                );
              }}
            >
              Reverse receipt
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            These quantities leave the stock of {po.location_name ?? 'the store'}. The PO lines go back to what stays received,
            and the item cost is worked out again without them.
          </p>
          <div className="space-y-2">
            {reverseGrn?.items.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-neutral-700 dark:text-neutral-200">
                  {i.item_name} <span className="text-xs text-neutral-500">· {i.quantity} {i.unit} still in</span>
                  {(i.serial_numbers?.length ?? 0) > 0 && <span className="block text-xs text-neutral-500">Serial numbers: whole line only</span>}
                </span>
                <div className="w-28">
                  <Input type="number" min={0} max={i.quantity} value={reverseQty[i.id] ?? ''}
                    disabled={i.quantity <= 0 || (i.serial_numbers?.length ?? 0) > 0}
                    onChange={(v) => setReverseQty((q) => ({ ...q, [i.id]: v }))} aria-label={`Quantity of ${i.item_name} to reverse`} />
                </div>
              </div>
            ))}
          </div>
          <Input value={reverseReason} onChange={setReverseReason} placeholder="Why is this receipt being reversed?" />
        </div>
      </Modal>

      <Modal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Purchase Order"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>Go Back</Button>
            <Button
              variant="danger"
              onClick={handleCancel}
              disabled={cancelMutation.isPending || !cancelReason.trim() || blockingBills.length > 0}
            >
              {cancelMutation.isPending ? 'Cancelling...' : 'Cancel PO'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Cancel <strong>{po.po_number}</strong> ({formatDocAmount(po.total_amount, currency)}). This action cannot be undone.
          </p>
          {blockingBills.length > 0 && (
            <p className="text-sm rounded-lg border border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 text-danger-700 dark:text-danger-300">
              This PO has vendor bills that are not cancelled:{' '}
              <strong>{blockingBills.map((b) => b.bill_number).join(', ')}</strong>. Cancel{' '}
              {blockingBills.length > 1 ? 'them' : 'it'} first.
            </p>
          )}
          {po.status === 'PARTIALLY_RECEIVED' && (
            <p className="text-sm rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 px-3 py-2 text-warning-700 dark:text-warning-300">
              Goods already received against this PO stay in inventory. Cancelling does not reverse
              that stock; only the unreceived quantities are dropped.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Reason <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Explain why this PO is being cancelled..."
              rows={3}
              className="w-full rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:border-danger-500 focus:ring-1 focus:ring-danger-500 outline-none transition-colors"
              maxLength={500}
            />
          </div>
        </div>
      </Modal>

      {closeItemIds !== undefined && (
        <CloseRemainingModal po={po} itemIds={closeItemIds ?? undefined} onClose={() => setCloseItemIds(undefined)} />
      )}

      {/* Notify Supplier Modal */}
      <Modal
        open={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
        title="Notify Supplier"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowNotifyModal(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleNotify}
              disabled={notifyMutation.isPending || !notifyRecipient.trim()}
            >
              {notifyMutation.isPending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Email <strong>{po.po_number}</strong> — every line with quantity and cost, plus the total — to the supplier.
          </p>
          {/* WhatsApp is hidden until an approved Meta template exists (API returns 400 for it). */}
          <RadioGroup
            label="Send Via"
            variant="horizontal"
            options={[{ value: 'EMAIL', label: 'Email' }]}
            value={notifyChannel}
            onChange={(v) => {
              setNotifyChannel(v as NotifyChannel);
              setNotifyRecipient(po.supplier_email ?? '');
            }}
          />
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              Email Address
            </label>
            <Input
              type="email"
              value={notifyRecipient}
              onChange={(v) => setNotifyRecipient(v)}
              placeholder="supplier@example.com"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PurchaseOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer
      title="Purchase Order Detail"
      breadcrumbs={[
        { label: 'Purchase' },
        { label: 'Purchase Orders', href: '/purchase-orders' },
        { label: id?.slice(0, 8).toUpperCase() ?? '' },
      ]}
    >
      <ErrorBoundary>
        <PurchaseOrderDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
